import { useState, useEffect } from "react";
import queryString from "query-string";

// services
import { Mixpanel, MixpanelEvents } from "../services/Mixpanel";

// utils
import { saveUTM } from "../utils";

/**
 * Custom hook to save UTM parameters to local storage
 * @param {string} locationSearch - The location.search string from props
 */
export const useSaveUTM = locationSearch => {
  useEffect(() => {
    if (locationSearch) {
      saveUTM(locationSearch);
    }
  }, [locationSearch]);
};

/**
 * Custom hook to track page view with Mixpanel
 * @param {string} pageName - Name of the page for tracking
 * @param {string} locationSearch - The location.search string from props
 * @param {string} deviceType - Device type (Mobile/Tablet/Desktop)
 */
export const usePageViewTracking = (
  pageName,
  locationSearch,
  deviceType,
  operatingSystem
) => {
  useEffect(() => {
    const loadTime =
      window.performance.timing.domContentLoadedEventEnd -
        window.performance.timing.navigationStart || 700;

    const utmParams = queryString.parse(locationSearch);

    // Mixpanel Event
    Mixpanel.track(MixpanelEvents.page_viewed, {
      page_name: pageName,
      asset: "India Website",
      asset_name: "India Corporate Website",
      load_time: loadTime,
      campaign_id: utmParams.campaignid,
      device_type: deviceType,
      operating_system: operatingSystem,
    });
  }, [pageName, locationSearch, deviceType]);
};

/**
 * Custom hook to detect and track device type
 * @returns {string} deviceType - Current device type (Mobile/Tablet/Desktop)
 */
export const useDeviceType = () => {
  const [deviceType, setDeviceType] = useState("");

  useEffect(() => {
    const updateDeviceType = () => {
      const width = window.innerWidth;
      if (width <= 768) {
        setDeviceType("Mobile");
      } else if (width <= 1024) {
        setDeviceType("Tablet");
      } else {
        setDeviceType("Desktop");
      }
    };

    // Update device type when the component mounts
    updateDeviceType();

    // Add an event listener to update device type when the window is resized
    window.addEventListener("resize", updateDeviceType);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", updateDeviceType);
    };
  }, []);

  return deviceType;
};

/**
 * Custom hook to detect operating system
 * @returns {string} operatingSystem - Current operating system
 */
export const useOperatingSystem = () => {
  const [operatingSystem, setOperatingSystem] = useState("");

  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    let os = "";

    if (userAgent.indexOf("Win") !== -1) {
      os = "Windows OS";
    } else if (userAgent.indexOf("Mac") !== -1) {
      os = "Mac OS";
    } else if (userAgent.indexOf("Linux") !== -1) {
      os = "Linux OS";
    } else if (userAgent.indexOf("Android") !== -1) {
      os = "Android OS";
    } else if (userAgent.indexOf("iOS") !== -1) {
      os = "iOS";
    } else {
      os = "Unknown";
    }

    setOperatingSystem(os);
  }, []);

  return operatingSystem;
};

/**
 * Custom hook to track page exit event with Mixpanel
 * @param {string} pageName - Name of the page for tracking
 */
export const usePageExitTracking = (pageName, deviceType, operatingSystem) => {
  useEffect(() => {
    const handlePageExit = () => {
      Mixpanel.track(MixpanelEvents.page_exit, {
        page_name: pageName,
        asset: "India Website",
        asset_name: "India Corporate Website",
        device_type: deviceType,
        operating_system: operatingSystem,
      });
    };

    window.addEventListener("beforeunload", handlePageExit);

    return () => {
      window.removeEventListener("beforeunload", handlePageExit);
    };
  }, [pageName]);
};

/**
 * Custom hook to track page depth (scroll depth) with Mixpanel
 * @param {string} pageName - Name of the page for tracking
 */
export const usePageDepthTracking = (pageName, deviceType, operatingSystem) => {
  const [depth25, setDepth25] = useState(true);
  const [depth50, setDepth50] = useState(true);
  const [depth75, setDepth75] = useState(true);
  const [depth100, setDepth100] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const pageYOffset = window.pageYOffset;
      const height = document.body.scrollHeight - window.innerHeight;
      const progress = Math.round((pageYOffset / height) * 100);

      if (progress === 25 && depth25) {
        Mixpanel.track(MixpanelEvents.page_depth_viewed, {
          page_name: pageName,
          asset: "India Website",
          asset_name: "India Corporate Website",
          page_depth: progress,
          device_type: deviceType,
          operating_system: operatingSystem,
        });
        setDepth25(false);
      } else if (progress === 50 && depth50) {
        Mixpanel.track(MixpanelEvents.page_depth_viewed, {
          page_name: pageName,
          asset: "India Website",
          asset_name: "India Corporate Website",
          page_depth: progress,
          device_type: deviceType,
          operating_system: operatingSystem,
        });
        setDepth50(false);
      } else if (progress === 75 && depth75) {
        Mixpanel.track(MixpanelEvents.page_depth_viewed, {
          page_name: pageName,
          asset: "India Website",
          asset_name: "India Corporate Website",
          page_depth: progress,
          device_type: deviceType,
          operating_system: operatingSystem,
        });
        setDepth75(false);
      } else if (progress === 100 && depth100) {
        Mixpanel.track(MixpanelEvents.page_depth_viewed, {
          page_name: pageName,
          asset: "India Website",
          asset_name: "India Corporate Website",
          page_depth: progress,
          device_type: deviceType,
          operating_system: operatingSystem,
        });
        setDepth100(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [pageName, depth25, depth50, depth75, depth100]);
};

/**
 * Combined hook that includes all common page tracking functionality
 * @param {string} pageName - Name of the page for tracking
 * @param {string} locationSearch - The location.search string from props
 * @returns {Object} - Returns deviceType and operatingSystem
 */
export const useCommonPageTracking = (pageName, locationSearch) => {
  const deviceType = useDeviceType();
  const operatingSystem = useOperatingSystem();

  useSaveUTM(locationSearch);
  usePageViewTracking(pageName, locationSearch, deviceType, operatingSystem);
  usePageDepthTracking(pageName, deviceType, operatingSystem);
  usePageExitTracking(pageName, deviceType, operatingSystem);

  return { deviceType, operatingSystem };
};
